/**
 * Admin API Service
 *
 * Handles all admin-specific API calls to the backend
 * Requires superadmin authentication
 */

import { supabase } from './supabase'
import type {
  DashboardStats,
  DashboardActivityResponse,
  UserGrowthData,
  ProcessingQueueResponse,
  ListUsersResponse,
  InviteUserResponse,
  ToggleSuperadminResponse,
  ListModelsResponse,
  DeleteModelResponse,
  SystemHealth,
  LogsResponse,
  ErrorSummary,
  PerformanceStats,
  AK47FireRequest,
  AK47FireResponse,
  AK47StatusResponse,
  AK47StatsResponse,
  UploadModelRequest,
  UploadModelResponse
} from '../types/api'

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>
}

class AdminAPI {
  private baseURL: string

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || '/api'
  }

  /**
   * Get access token from current session
   */
  async getAccessToken(): Promise<string | undefined> {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token
  }

  /**
   * Make authenticated request to admin endpoints
   */
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const token = await this.getAccessToken()

    if (!token) {
      throw new Error('Not authenticated')
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }

    // Add timeout to prevent infinite hanging
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 30000) // 30 second timeout

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal
      })

      clearTimeout(timeout)

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }))
        throw new Error(error.detail || `Request failed with status ${response.status}`)
      }

      return response.json()
    } catch (error) {
      clearTimeout(timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - server not responding')
      }
      throw error
    }
  }

  // ============================================================================
  // DASHBOARD ENDPOINTS
  // ============================================================================

  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('/admin/dashboard/stats')
  }

  async getDashboardActivity(limit: number = 50): Promise<DashboardActivityResponse> {
    return this.request<DashboardActivityResponse>(`/admin/dashboard/activity?limit=${limit}`)
  }

  async getUserGrowth(days: number = 30): Promise<UserGrowthData[]> {
    return this.request<UserGrowthData[]>(`/admin/dashboard/user-growth?days=${days}`)
  }

  async getProcessingQueue(): Promise<ProcessingQueueResponse> {
    return this.request<ProcessingQueueResponse>('/admin/dashboard/processing-queue')
  }

  // ============================================================================
  // USER MANAGEMENT ENDPOINTS
  // ============================================================================

  async listUsers(params: { limit?: number; offset?: number; search?: string } = {}): Promise<ListUsersResponse> {
    const { limit = 50, offset = 0, search = '' } = params
    const query = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      search
    }).toString()
    return this.request<ListUsersResponse>(`/admin/users/list?${query}`)
  }

  async inviteUser(email: string): Promise<InviteUserResponse> {
    return this.request<InviteUserResponse>('/admin/users/invite', {
      method: 'POST',
      body: JSON.stringify({ email })
    })
  }

  async toggleSuperadmin(userId: string): Promise<ToggleSuperadminResponse> {
    return this.request<ToggleSuperadminResponse>('/admin/users/toggle-superadmin', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    })
  }

  // ============================================================================
  // MODEL MANAGEMENT ENDPOINTS
  // ============================================================================

  async listModels(params: { limit?: number; offset?: number; status?: string } = {}): Promise<ListModelsResponse> {
    const { limit = 50, offset = 0, status } = params
    const queryParams: Record<string, string> = {
      limit: limit.toString(),
      offset: offset.toString()
    }
    if (status) {
      queryParams.status = status
    }
    const query = new URLSearchParams(queryParams).toString()
    return this.request<ListModelsResponse>(`/admin/models/list?${query}`)
  }

  async deleteModel(modelId: string): Promise<DeleteModelResponse> {
    return this.request<DeleteModelResponse>(`/admin/models/${modelId}`, {
      method: 'DELETE'
    })
  }

  // ============================================================================
  // SYSTEM MONITORING ENDPOINTS
  // ============================================================================

  async getSystemHealth(): Promise<SystemHealth> {
    return this.request<SystemHealth>('/admin/system/health')
  }

  async getLogs(params: {
    limit?: number;
    level?: string;
    category?: string;
    operation?: string;
    hours?: number
  } = {}): Promise<LogsResponse> {
    const { limit = 100, level = '', category = '', operation = '', hours = 24 } = params
    const query = new URLSearchParams({
      limit: limit.toString(),
      level,
      category,
      operation,
      hours: hours.toString()
    }).toString()
    return this.request<LogsResponse>(`/admin/logs?${query}`)
  }

  async getErrorSummary(hours: number = 24): Promise<ErrorSummary[]> {
    return this.request<ErrorSummary[]>(`/admin/logs/errors?hours=${hours}`)
  }

  async getPerformanceStats(hours: number = 24): Promise<{ performance_stats: PerformanceStats }> {
    return this.request<{ performance_stats: PerformanceStats }>(`/admin/logs/performance?hours=${hours}`)
  }

  // ============================================================================
  // AK47 STRESS TESTING ENDPOINTS
  // ============================================================================

  async startAK47(params: AK47FireRequest): Promise<AK47FireResponse> {
    return this.request<AK47FireResponse>('/ak47/fire', {
      method: 'POST',
      body: JSON.stringify(params)
    })
  }

  async stopAK47(sessionId: string): Promise<{ status: string; payloads_fired: number; message: string }> {
    return this.request<{ status: string; payloads_fired: number; message: string }>(`/ak47/cease-fire/${sessionId}`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  async getAK47Status(sessionId: string): Promise<AK47StatusResponse> {
    return this.request<AK47StatusResponse>(`/ak47/status/${sessionId}`)
  }

  async getAK47Stats(): Promise<AK47StatsResponse> {
    return this.request<AK47StatsResponse>('/ak47/stats')
  }

  // ============================================================================
  // MODEL UPLOAD ENDPOINTS
  // ============================================================================

  async uploadModel(uploadData: UploadModelRequest): Promise<UploadModelResponse> {
    const { file, modelName, modelArchitecture, metadataFile, modelGraphFile, constMetaFile } = uploadData
    const token = await this.getAccessToken()

    if (!token) {
      throw new Error('Not authenticated')
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('model_name', modelName)
    formData.append('model_architecture', modelArchitecture)
    formData.append('metadata_file', metadataFile)
    formData.append('model_graph_file', modelGraphFile)
    if (constMetaFile) {
      formData.append('const_meta_file', constMetaFile)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 120000) // 2 minute timeout for uploads

    try {
      const response = await fetch(`${this.baseURL}/models/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: controller.signal
      })

      clearTimeout(timeout)

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
        throw new Error(error.detail || `Upload failed with status ${response.status}`)
      }

      return response.json()
    } catch (error) {
      clearTimeout(timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Upload timeout - please try again')
      }
      throw error
    }
  }
}

export const adminApi = new AdminAPI()
